import {
	CLAUDE_COLOR,
	DEEP_SEEK_COLOR,
	GEMINI_COLOR,
	GEMMA_COLOR,
	GLM_COLOR,
	GPT_COLOR,
	GROK_COLOR,
	KIMI_COLOR,
	MOONSHOT_COLOR,
	NANO_BANANA_COLOR,
	NOVA_COLOR,
	STABLE_DIFFUSION_COLOR,
	UNKNOWN_MODEL_COLOR,
} from "@src/common/constants";
import type { SupportedModel } from "@src/gql/graphql";

export type ModelProvider =
	| "gpt"
	| "claude"
	| "gemini"
	| "gemma"
	| "moonshot"
	| "nova"
	| "deepSeek"
	| "grok"
	| "kimi"
	| "glm"
	| "stableDiffusion"
	| "nanoBanana"
	| "unknown";

const PROVIDER_SHORT_NAME: Record<ModelProvider, string> = {
	gpt: "by GPT",
	claude: "by Claude",
	gemini: "by Gemini",
	gemma: "by Gemma",
	moonshot: "by Moonshot",
	nova: "by Nova",
	deepSeek: "by DeepSeek",
	grok: "by Grok",
	kimi: "by Kimi",
	glm: "by GLM",
	stableDiffusion: "by Stable Diffusion",
	nanoBanana: "by Nano Banana",
	unknown: "Unknown",
};

const PROVIDER_COLOR: Record<ModelProvider, string> = {
	gpt: GPT_COLOR,
	claude: CLAUDE_COLOR,
	gemini: GEMINI_COLOR,
	gemma: GEMMA_COLOR,
	moonshot: MOONSHOT_COLOR,
	nova: NOVA_COLOR,
	deepSeek: DEEP_SEEK_COLOR,
	grok: GROK_COLOR,
	kimi: KIMI_COLOR,
	glm: GLM_COLOR,
	stableDiffusion: STABLE_DIFFUSION_COLOR,
	nanoBanana: NANO_BANANA_COLOR,
	unknown: UNKNOWN_MODEL_COLOR,
};

/** Multi-word brand fragments joined after underscore split. */
const COMPOUND_BRANDS: ReadonlyArray<readonly [string, string, string]> = [
	["Deep", "Seek", "DeepSeek"],
	["Stable", "Diffusion", "Stable Diffusion"],
	["Stable", "Image", "Stable Image"],
	["Amazon", "Nova", "Amazon Nova"],
	["Nano", "Banana", "Nano Banana"],
];

const UPPERCASE_BRANDS = new Set(["GPT", "GLM"]);

const IMAGE_MODEL_PREFIXES = [
	"STABLE_DIFFUSION",
	"STABLE_IMAGE",
	"NOVA_CANVAS",
	"AMAZON_NOVA_CANVAS",
	"NANO_BANANA",
	"GPT_IMAGE",
	"IMAGEN",
	"DALL_E",
] as const;

export const resolveModelProvider = (model: SupportedModel): ModelProvider => {
	const id = String(model).toUpperCase();

	if (id.startsWith("GPT") || id.startsWith("O1") || id.startsWith("O3")) {
		return "gpt";
	}
	if (id.startsWith("CLAUDE")) {
		return "claude";
	}
	if (id.startsWith("GEMINI")) {
		return "gemini";
	}
	if (id.startsWith("GEMMA")) {
		return "gemma";
	}
	if (id.startsWith("MOONSHOT")) {
		return "moonshot";
	}
	if (id.startsWith("NOVA") || id.startsWith("AMAZON_NOVA")) {
		return "nova";
	}
	if (id.startsWith("DEEP_SEEK") || id.startsWith("DEEPSEEK")) {
		return "deepSeek";
	}
	if (id.startsWith("GROK")) {
		return "grok";
	}
	if (id.startsWith("KIMI")) {
		return "kimi";
	}
	if (id.startsWith("GLM")) {
		return "glm";
	}
	if (id.startsWith("STABLE_DIFFUSION") || id.startsWith("STABLE_IMAGE")) {
		return "stableDiffusion";
	}
	if (id.startsWith("NANO_BANANA")) {
		return "nanoBanana";
	}

	return "unknown";
};

export const resolveModelIconProvider = (
	model: SupportedModel,
): ModelProvider => {
	const id = String(model).toUpperCase();
	if (id.startsWith("IMAGEN")) {
		return "gemini";
	}
	return resolveModelProvider(model);
};

export const getModelProviderColor = (model: SupportedModel): string =>
	PROVIDER_COLOR[resolveModelProvider(model)];

export const getModelProviderShortName = (model: SupportedModel): string =>
	PROVIDER_SHORT_NAME[resolveModelProvider(model)];

/** True when the model id is an image generator (prefix or `*_IMAGE`). */
export const isImageGenerationModel = (
	model: SupportedModel | string | null | undefined,
): boolean => {
	if (!model) {
		return false;
	}

	const id = String(model).toUpperCase();
	if (IMAGE_MODEL_PREFIXES.some((prefix) => id.startsWith(prefix))) {
		return true;
	}

	// Gemini Flash/Pro Image, etc. — not covered by IMAGEN_* prefixes.
	return id.includes("_IMAGE");
};

const titleCase = (token: string): string => {
	if (!token) {
		return token;
	}
	return token.charAt(0) + token.slice(1).toLowerCase();
};

/**
 * Version digit runs:
 * - 1 digit → "5"
 * - 2 digits → "5.2" / "3.1"
 * - 3 digits → "4.2" (strip trailing zeros: 420 → 4.2)
 * - 4+ digits → keep as-is (date-like suffixes: 0528, 0324)
 */
const formatVersionDigits = (digits: string): string => {
	if (digits.length <= 1) {
		return digits;
	}
	if (digits.length === 2) {
		return `${digits[0]}.${digits[1]}`;
	}
	if (digits.length === 3) {
		const major = digits[0];
		const rest = digits.slice(1).replace(/0+$/, "") || "0";
		return `${major}.${rest}`;
	}
	return digits;
};

const formatBrandToken = (brand: string): string => {
	const upper = brand.toUpperCase();
	if (UPPERCASE_BRANDS.has(upper)) {
		return upper;
	}
	return titleCase(brand);
};

/**
 * Split LETTERS+DIGITS into brand + version words.
 * Single-letter brands glue to the version (R1, V3.2, K2.5, o1).
 * Long digit runs on single-letter brands: first digit is the
 * version, the rest stays as a date-like suffix (R10528 → R1 0528).
 */
const pushBrandAndDigits = (
	words: string[],
	brand: string,
	digits: string,
): void => {
	const upperBrand = brand.toUpperCase();

	if (upperBrand.length === 1) {
		const letter = upperBrand === "O" ? "o" : upperBrand;
		if (digits.length >= 4) {
			words.push(`${letter}${digits[0]}`);
			words.push(digits.slice(1));
			return;
		}
		words.push(`${letter}${formatVersionDigits(digits)}`);
		return;
	}

	words.push(formatBrandToken(brand));
	words.push(formatVersionDigits(digits));
};

const joinDisplayWords = (words: string[]): string => {
	if (words.length === 0) {
		return "Unknown";
	}

	const merged: string[] = [];
	for (let i = 0; i < words.length; i++) {
		const word = words[i];
		if (word === undefined) {
			continue;
		}
		const next = words[i + 1];
		const compound = COMPOUND_BRANDS.find(
			([left, right]) => word === left && next === right,
		);
		if (compound) {
			merged.push(compound[2]);
			i++;
			continue;
		}
		merged.push(word);
	}

	// GPT + version → GPT-5.2 / GPT-o1 (+ remaining tokens)
	const [first, second, ...rest] = merged;
	if (
		first === "GPT" &&
		second &&
		(/^\d/.test(second) || /^o\d/i.test(second))
	) {
		return [`GPT-${second}`, ...rest].join(" ");
	}

	return merged.join(" ");
};

/**
 * Convert a model id to a display label with general rules only —
 * no per-model hardcoding. Keeps every underscore token.
 *
 * Examples:
 * - GEMINI31_FLASH_LIGHT → "Gemini 3.1 Flash Light"
 * - GEMINI31_FLASH_LIGHT_PREVIEW → "Gemini 3.1 Flash Light Preview"
 * - GPT52 → "GPT-5.2"
 * - DEEP_SEEK_R10528 → "DeepSeek R1 0528"
 */
export const formatModelDisplayName = (model: SupportedModel): string => {
	const id = String(model).toUpperCase();
	const parts = id.split("_").filter(Boolean);
	const words: string[] = [];

	for (const part of parts) {
		const branded = /^([A-Z]+?)(\d+)$/.exec(part);
		if (branded) {
			const brand = branded[1];
			const digits = branded[2];
			if (brand && digits) {
				pushBrandAndDigits(words, brand, digits);
			}
			continue;
		}
		if (/^\d+$/.test(part)) {
			words.push(formatVersionDigits(part));
			continue;
		}
		if (/^\d+[A-Z]+$/.test(part)) {
			words.push(part);
			continue;
		}
		words.push(formatBrandToken(part));
	}

	return joinDisplayWords(words);
};

export const getModelDisplayName = (model: SupportedModel): string =>
	formatModelDisplayName(model);
